import {
  Link,
  PasswordCriterias,
  Form,
  InputCheckbox,
  InputPassword,
  InputText,
  useFormConfig,
  FormBloc,
  FormField,
  FormRow,
  FormSubTitle,
} from "@endya-conformite/ui-kit";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import { DeepPartial } from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import style from "./SignupForm.module.scss";
import { passwordWithConfirmationSchema } from "./password.definition";

const errorMessage = {
  email: "Veuillez renseigner une adresse mail valide",
  confirmEmail:
    "La confirmation de votre adresse email ne correspond pas à la valeur du champ Adresse email",
};

const schema = zod.object({
  consentCGV: zod.literal(true, {
    errorMap: () => ({
      message:
        "Vous devez accepter les conditions générales de vente, la politique de confidentialité et la gestion des cookies pour créer un compte",
    }),
  }),
  consentNewsletter: zod.boolean().optional(),
  email: zod
    .object({
      email: zod
        .string()
        .nonempty(errorMessage.email)
        .email(errorMessage.email),
      confirm: zod.string().nonempty(errorMessage.confirmEmail),
    })
    .refine((data) => data.email === data.confirm, {
      message: errorMessage.confirmEmail,
      path: ["confirm"],
    }),
  password: passwordWithConfirmationSchema,
});

export type SignupFormValues = zod.infer<typeof schema>;

type SignupFormProps = {
  formId: string;
  onSubmit: (values: SignupFormValues) => unknown;
  defaultValues?: DeepPartial<SignupFormValues>;
};

export function SignupForm({
  formId,
  onSubmit,
  defaultValues,
}: SignupFormProps) {
  const formConfig = useFormConfig<typeof schema, SignupFormValues>({
    schema,
    defaultValues,
  });

  const passwordValue = formConfig.watch("password.password");

  const consentCGVLabelWithLinks = (
    <span>
      J&apos;accepte les{" "}
      <Link
        to="https://endya-public.s3.eu-west-3.amazonaws.com/cgu.pdf"
        target="_blank"
      >
        <span
          aria-hidden
          className={style.legalLink}
          onClick={(e) => e.stopPropagation()}
        >
          conditions générales de vente
        </span>
      </Link>
      , la{" "}
      <Link
        to="https://endya-public.s3.eu-west-3.amazonaws.com/politique_confidentilite.pdf"
        target="_blank"
      >
        <span
          className={style.legalLink}
          aria-hidden
          onClick={(e) => e.stopPropagation()}
        >
          politique de confidentialité
        </span>
      </Link>{" "}
      et la{" "}
      <Link
        to="https://endya-public.s3.eu-west-3.amazonaws.com/gestion_cookies.pdf"
        target="_blank"
      >
        <span
          className={style.legalLink}
          aria-hidden
          onClick={(e) => e.stopPropagation()}
        >
          gestion des cookies.
        </span>
      </Link>
    </span>
  );

  return (
    <Form formConfig={formConfig} onSubmit={onSubmit} id={formId}>
      <FormRow>
        <FormField>
          <InputText<SignupFormValues>
            name="email.email"
            label="Adresse email"
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputText<SignupFormValues>
            name="email.confirm"
            label="Confirmer l'adresse email"
            required
          />
        </FormField>
      </FormRow>
      <FormSubTitle>
        <h2>Choisissez votre mot de passe</h2>
      </FormSubTitle>
      <FormRow>
        <FormField className={style.passwordContainer}>
          <InputPassword<SignupFormValues>
            name="password.password"
            label="Mot de passe"
            required
          />
        </FormField>
      </FormRow>
      <FormBloc>
        <PasswordCriterias password={passwordValue} />
      </FormBloc>
      <FormRow>
        <FormField>
          <InputPassword<SignupFormValues>
            name="password.confirm"
            label="Confirmez votre mot de passe"
            required
          />
        </FormField>
      </FormRow>
      <FormSubTitle>
        <h2>Consentement</h2>
      </FormSubTitle>
      <FormRow>
        <FormField>
          <InputCheckbox<SignupFormValues>
            size="medium"
            name="consentCGV"
            label={consentCGVLabelWithLinks}
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <div>
            <InputCheckbox<SignupFormValues>
              name="consentNewsletter"
              label="Je souhaite recevoir la newsletter d'Endya."
            />
            <small>
              En cochant cette case, vous acceptez de recevoir notre newsletter.
              Plus d&apos;informations sur l&apos;usage de vos données{" "}
              <Link
                className={style.legalLink}
                to="https://www.endya.fr/politique-de-confidentialite"
                target="_blank"
              >
                ici
              </Link>
              .
            </small>
          </div>
        </FormField>
      </FormRow>
    </Form>
  );
}
